import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { apiRequest } from '../config/api';
import {
  Box, Typography, Select, MenuItem, Button,
  TextField, CircularProgress, Grid, Paper, Autocomplete, Alert, Chip,
  Tabs, Tab, IconButton, Tooltip, Snackbar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Document } from '../types';
import QuickReplayNavigation from '../components/QuickReplayNavigation';

const fontFamily = 'Inter, Open Sans, Arial, sans-serif';

interface ChatMessage {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
  document_id?: number | string;
  processing_time?: number;
}

export default function ReplayCenter() {
  const [searchParams] = useSearchParams();
  const [analysts, setAnalysts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [sectors, setSectors] = useState<any[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedAnalyst, setSelectedAnalyst] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedSector, setSelectedSector] = useState<number | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<number | string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [provider, setProvider] = useState<'openai' | 'gemini' | 'xai'>('gemini');
  const [language, setLanguage] = useState<'English' | 'Hebrew'>('English');
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [summary, setSummary] = useState<string>('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [transcriptLines, setTranscriptLines] = useState<{ time: number | null; text: string }[]>([]);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLDivElement>(null);
  const lastScrolledLineRef = useRef<HTMLElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // --- State Persistence Logic ---
  interface DocState {
    summary: string;
    chatHistory: ChatMessage[];
    suggestions: string[];
  }
  const [docStates, setDocStates] = useState<Record<string, DocState>>({});
  const prevKeyRef = useRef<string>('');
  const stateRef = useRef({ summary, chatHistory, suggestions });

  // Keep stateRef up to date
  useEffect(() => {
    stateRef.current = { summary, chatHistory, suggestions };
  }, [summary, chatHistory, suggestions]);

  // Handle caching and switching state between docs/languages
  useEffect(() => {
    if (!selectedDocId) return;

    const currentKey = `${selectedDocId}_${language}`;
    const prevKey = prevKeyRef.current;

    // Only run when the context (doc or language) actually changes
    if (currentKey !== prevKey) {
      // 1. Save state for the PREVIOUS context
      if (prevKey) {
        const { summary, chatHistory, suggestions } = stateRef.current;
        setDocStates(prev => ({
          ...prev,
          [prevKey]: { summary, chatHistory, suggestions }
        }));
      }

      // 2. Load state for the NEW context (or reset if not found)
      // Note: We use the functional update of setDocStates to ensure we don't lose pending updates?
      // Actually, for reading, we rely on the closure variable 'docStates'.
      // Since switches don't happen in rapid succession (<16ms), this is generally safe.
      const cached = docStates[currentKey];
      if (cached) {
        setSummary(cached.summary);
        setChatHistory(cached.chatHistory);
        setSuggestions(cached.suggestions);
      } else {
        setSummary('');
        setChatHistory([]);
        setSuggestions([]);
      }

      // 3. Update current key
      prevKeyRef.current = currentKey;
    }
  }, [selectedDocId, language, docStates]); // Added docStates to ensure we have latest cache

  const handleCopySummary = () => {
    if (summary) {
      navigator.clipboard.writeText(summary);
      setSnackbarOpen(true);
    }
  };

  // Copy transcript to clipboard
  const handleCopyTranscript = async () => {
    if (!selectedDoc?.text_content) {
      alert(language === 'Hebrew' ? 'אין תמליל להעתקה' : 'No transcript to copy');
      return;
    }

    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(selectedDoc.text_content);
      setSnackbarOpen(true);
    } catch (err) {
      // Fallback for mobile/older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = selectedDoc.text_content;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setSnackbarOpen(true);
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
        alert(language === 'Hebrew' ? 'העתקה נכשלה' : 'Copy failed');
      }
    }
  };

  // Export transcript to PDF with logo - with mobile fallback
  const handleExportTranscriptPDF = () => {
    if (!selectedDoc?.text_content) return;

    const isHebrewContent = containsHebrew(selectedDoc.text_content);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${selectedDoc.title || 'Transcript'}</title>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body {
            font-family: ${isHebrewContent ? "'Segoe UI', Tahoma, Arial" : "'Inter', 'Open Sans', Arial"}, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
            line-height: 1.8;
            color: #2c3e50;
            direction: ${isHebrewContent ? 'rtl' : 'ltr'};
          }
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #1A2341;
          }
          .logo { height: 50px; }
          .title { color: #1A2341; font-size: 24px; font-weight: 700; margin: 0; }
          .meta { color: #666; font-size: 14px; margin-bottom: 20px; }
          .content { white-space: pre-wrap; word-break: break-word; }
          .timestamp { color: #4A90E2; font-family: monospace; margin-right: 10px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #999; font-size: 12px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 class="title">${selectedDoc.title || 'Transcript'}</h1>
            <div class="meta">
              ${selectedDoc.filename ? `File: ${selectedDoc.filename}` : ''}
              ${selectedDoc.created_at ? ` | Date: ${new Date(selectedDoc.created_at).toLocaleDateString()}` : ''}
            </div>
          </div>
        </div>
        <div class="content">${selectedDoc.text_content.replace(/\[(\d+:\d+)\]/g, '<span class="timestamp">[$1]</span>').replace(/\n/g, '<br>')}</div>
        <div class="footer">
          Generated by Psagot Research Platform | ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;

    // Download as HTML file (user can open in browser and print/save as PDF)
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(selectedDoc.title || 'transcript').replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Show helpful message
    const message = language === 'Hebrew'
      ? 'הקובץ הורד. פתח אותו בדפדפן ובחר Print > Save as PDF'
      : 'File downloaded. Open in browser and select Print > Save as PDF';

    if (isMobile) {
      alert(message);
    } else {
      // On desktop, also offer to open print dialog
      const openPrint = window.confirm(message + (language === 'Hebrew' ? '\n\nלפתוח חלון הדפסה עכשיו?' : '\n\nOpen print dialog now?'));
      if (openPrint) {
        const printWindow = window.open('', '', 'width=800,height=600');
        if (printWindow) {
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          setTimeout(() => {
            printWindow.print();
          }, 250);
        }
      }
    }
  };

  // Parse transcript when document changes
  useEffect(() => {
    if (selectedDoc && selectedDoc.text_content) {
      const content = selectedDoc.text_content;
      
      // Find all timestamp matches (support [MM:SS], [MMM:SS], and [HH:MM:SS])
      // Changed from \d{1,2} to \d+ to support durations >= 100 minutes
      const timestampRegex = /\[(\d+):(\d{2})(?::(\d{2}))?\]/g;
      const matches = Array.from(content.matchAll(timestampRegex));
      
      // If no timestamps found, fall back to line splitting
      if (matches.length === 0) {
        const lines = content.split('\n')
          .map(line => line.trim())
          .filter(line => line)
          .map(line => ({ time: null, text: line }));
        setTranscriptLines(lines);
        return;
      }

      const newLines: { time: number | null; text: string }[] = [];
      
      // Handle text before the first timestamp
      if (matches[0].index && matches[0].index > 0) {
        const intro = content.substring(0, matches[0].index).trim();
        if (intro) {
          newLines.push({ time: null, text: intro });
        }
      }

      // Process each timestamp segment
      for (let i = 0; i < matches.length; i++) {
        const match = matches[i];
        const nextMatch = matches[i + 1];
        
        // Calculate seconds
        // Match groups: 1=HH/MM, 2=MM/SS, 3=SS (optional)
        let seconds = 0;
        if (match[3]) {
          // Format: HH:MM:SS
          seconds = parseInt(match[1]) * 3600 + parseInt(match[2]) * 60 + parseInt(match[3]);
        } else {
          // Format: MM:SS
          seconds = parseInt(match[1]) * 60 + parseInt(match[2]);
        }
        
        // Extract text between this timestamp and the next (or end of string)
        const start = match.index! + match[0].length;
        const end = nextMatch ? nextMatch.index : content.length;
        const text = content.substring(start, end).trim();
        
        // Only add if there is text or it's a timestamp marker
        newLines.push({ time: seconds, text });
      }
      
      setTranscriptLines(newLines);
    } else {
      setTranscriptLines([]);
    }
  }, [selectedDoc]);

  // Scroll transcript to active line (smart auto-scroll)
  useEffect(() => {
    if (isPlaying && activeLineRef.current && transcriptRef.current) {
      const container = transcriptRef.current;
      const activeLine = activeLineRef.current;
      
      // Only attempt scroll if the active line has changed
      if (lastScrolledLineRef.current !== activeLine) {
        const containerRect = container.getBoundingClientRect();
        const activeLineRect = activeLine.getBoundingClientRect();
        
        // Check if the active line is currently visible in the viewport
        const isVisible = (
          activeLineRect.top >= containerRect.top &&
          activeLineRect.bottom <= containerRect.bottom
        );

        // Check distance from center to determine if user has scrolled far away
        const containerCenter = containerRect.top + containerRect.height / 2;
        const lineCenter = activeLineRect.top + activeLineRect.height / 2;
        const distanceFromCenter = Math.abs(containerCenter - lineCenter);
        
        // Heuristic: If the user has scrolled far away (> 400px), assume they are reading history
        // and DO NOT auto-scroll. 
        const isFarAway = distanceFromCenter > 400;

        // Only auto-scroll if:
        // 1. The line is NOT currently visible (so we don't disturb reading adjacent lines)
        // 2. AND the user hasn't scrolled far away (so we don't jump them back from history)
        if (!isVisible && !isFarAway) {
          activeLine.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
        
        lastScrolledLineRef.current = activeLine;
      }
    }
  }, [currentTime, isPlaying]);

  const handleTranscriptClick = (time: number | null) => {
    if (time !== null && audioPlayer) {
      audioPlayer.currentTime = time;
      setCurrentTime(time);
      if (!isPlaying) {
        audioPlayer.play();
        setIsPlaying(true);
      }
    }
  };

  // Fetch all filter data on mount
  useEffect(() => {
    supabase.from('analysts').select('*').then(({ data }) => setAnalysts(data || []));
    supabase.from('companies').select('*').then(({ data }) => setCompanies(data || []));
    supabase.from('sectors').select('*').then(({ data }) => setSectors(data || []));
    // Fetch documents with lightweight columns only (text_content/layout_data are too large for bulk fetch)
    supabase.from('documents').select('id, title, doc_type, storage_url, company_id, sector_id, filename, created_at, report_date, analyst_names, company_tickers, sector, subsector').then(({ data }) => setDocuments(data || []));
  }, []);

  // Fetch text_content separately when a document is selected
  useEffect(() => {
    if (!selectedDocId) return;
    supabase.from('documents').select('text_content').eq('id', selectedDocId).single().then(({ data }) => {
      if (data?.text_content) {
        setDocuments(prev => prev.map(d =>
          d.id === selectedDocId ? { ...d, text_content: data.text_content } : d
        ));
        // Also update selectedDoc if it's already set
        setSelectedDoc(prev => prev?.id === selectedDocId ? { ...prev, text_content: data.text_content } : prev);
      }
    });
  }, [selectedDocId]);

  // Handle URL params for direct document navigation
  useEffect(() => {
    const docIdParam = searchParams.get('docId') || searchParams.get('doc');
    if (docIdParam && documents.length > 0) {
      // Support both UUID strings and legacy integer IDs
      const docId = docIdParam; // Keep as string for UUID support
      if (docId && selectedDocId !== docId) {
         setSelectedDocId(docId);
         // Clear filters to ensure visibility
         setSelectedAnalyst(null);
         setSelectedCompany(null);
         setSelectedSector(null);
         setSelectedTicker(null);
      }
    }
  }, [searchParams, documents, selectedDocId]);

  // Handle deep-link: ?t=seconds for audio seek and #para-N for transcript scroll
  useEffect(() => {
    // Seek audio to timestamp from ?t= param
    const tParam = searchParams.get('t');
    if (tParam && audioPlayer) {
      const seekTime = parseInt(tParam, 10);
      if (!isNaN(seekTime) && seekTime >= 0) {
        // Wait for audio to be ready before seeking
        const trySeek = () => {
          if (audioPlayer.readyState >= 1) {
            audioPlayer.currentTime = seekTime;
          } else {
            audioPlayer.addEventListener('loadedmetadata', () => {
              audioPlayer.currentTime = seekTime;
            }, { once: true });
            audioPlayer.load();
          }
        };
        trySeek();
      }
    }

    // Scroll to paragraph from #para-N hash
    const hash = window.location.hash;
    const paraMatch = hash.match(/^#para-(\d+)$/);
    if (paraMatch && transcriptLines.length > 0) {
      const paraIndex = parseInt(paraMatch[1], 10);
      // Find the transcript line element and scroll to it
      const paraEl = document.getElementById(`transcript-para-${paraIndex}`);
      if (paraEl) {
        paraEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        paraEl.style.backgroundColor = 'rgba(74, 144, 226, 0.15)';
        setTimeout(() => { paraEl.style.backgroundColor = ''; }, 3000);
      }
    }
  }, [searchParams, audioPlayer, transcriptLines]);

  // Filter logic - only show audio documents
  const filteredCompanies = companies.filter(company =>
    (!selectedAnalyst || company.analyst_id === selectedAnalyst) &&
    (!selectedSector || company.sector_id === selectedSector)
  );
  const filteredTickerOptions = Array.from(new Set(filteredCompanies.map(c => c.ticker))).filter(Boolean);
  const docIdParam = searchParams.get('docId') || searchParams.get('doc');
  const filteredDocuments = documents.filter(doc => {
    // Always include the directly-linked document from URL
    if (docIdParam && doc.id === docIdParam) return true;

    // Must be an audio/video/transcript document
    if (!['Audio', 'audio', 'video', 'Video', 'transcript', 'media'].includes(doc.doc_type || '')) return false;

    // Apply filters (analyst_id is not on documents table, so skip it)
    if (selectedCompany && doc.company_id !== selectedCompany) return false;
    if (selectedSector && doc.sector_id !== selectedSector) return false;
    if (selectedTicker && companies.find(c => c.id === doc.company_id)?.ticker !== selectedTicker) return false;

    return true;
  });

  // When ticker changes, auto-select the corresponding company
  useEffect(() => {
    if (selectedTicker) {
      const company = companies.find(c => c.ticker === selectedTicker);
      setSelectedCompany(company ? company.id : null);
    }
  }, [selectedTicker, companies]);

  // Update selected document when selectedDocId changes
  useEffect(() => {
    // Try filtered first, fall back to all documents (for direct URL navigation)
    const newDoc = filteredDocuments.find(d => d.id === selectedDocId)
      || documents.find(d => d.id === selectedDocId)
      || null;

    // Only reset if the document ID actually changed
    if (newDoc?.id !== selectedDoc?.id) {
      setSelectedDoc(newDoc);
    }

    if (newDoc) {
      setError(null);
    }
  }, [selectedDocId, filteredDocuments, documents, selectedDoc?.id]);

  // Scroll chat to bottom on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Audio player setup
  useEffect(() => {
    // Stop any currently playing audio immediately when selectedDoc changes
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.src = ''; // Release resources immediately
    }
    
    // Reset UI state
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setAudioPlayer(null);

    if (selectedDoc && selectedDoc.storage_url) {
      const audio = new Audio();
      
      // Add error handling for audio loading
      const onError = (e: any) => {
        console.error('Audio loading error:', e);
        setError(`Failed to load audio file. The file may not exist or may be corrupted.`);
        setAudioPlayer(null);
      };
      
      const onLoadedMetadata = () => {
        if (isMounted) {
          setDuration(audio.duration);
          setError(null); // Clear any previous errors
        }
      };
      
      const onTimeUpdate = () => {
        if (isMounted) {
          setCurrentTime(audio.currentTime);
        }
      };
      
      const onEnded = () => {
        if (isMounted) {
          setIsPlaying(false);
          setCurrentTime(0);
        }
      };
      
      const onCanPlay = () => {
        if (isMounted) {
          setError(null); // Clear errors when audio is ready
        }
      };

      let isMounted = true;
      
      // Set up event listeners BEFORE setting src to catch errors early
      audio.addEventListener('error', onError);
      audio.addEventListener('loadedmetadata', onLoadedMetadata);
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);
      audio.addEventListener('canplay', onCanPlay);
      
      // Set src after listeners are attached
      // Use preload="none" to prevent automatic loading
      audio.preload = 'none';
      audio.src = selectedDoc.storage_url;
      
      setAudioPlayer(audio);
      
      return () => {
        isMounted = false;
        audio.pause();
        audio.src = ''; // Release resources
        audio.removeEventListener('error', onError);
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('canplay', onCanPlay);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc]);

  const togglePlayPause = () => {
    if (audioPlayer) {
      if (isPlaying) {
        audioPlayer.pause();
      } else {
        audioPlayer.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const askQuestion = async () => {
    if (!question.trim() || !selectedDocId) return;

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      question: question,
      answer: '',
      timestamp: new Date(),
      document_id: selectedDocId
    };

    setChatHistory(prev => [newMessage, ...prev]);
    setLoading(true);
    setError(null);

    try {
      console.log('Sending question:', question);
      console.log('Selected doc ID:', selectedDocId);
      
      const data = await apiRequest('/documents/audio-question', {
        method: 'POST',
        body: JSON.stringify({
          document_id: selectedDocId,
          question: question,
          language: language,
          provider: provider
        }),
      });
      
      console.log('API Response:', data);
      
      // Handle new structured response format
      const answer = data.answer || data.answer_text || data.summary || '';
      const processingTime = data.processing_time || 0;
      
      console.log('Extracted answer:', answer);
      console.log('Processing time:', processingTime);
      
      // Update the message with the answer
      setChatHistory(prev => {
        try {
          return prev.map(msg => 
            msg.id === newMessage.id 
              ? { ...msg, answer: answer, processing_time: processingTime }
              : msg
          );
        } catch (error) {
          console.error('Error updating chat history:', error);
          return prev;
        }
      });
      
      // Show provider info if available
      if (data.provider_used && data.provider_used !== provider) {
        setError(`Switched to ${data.provider_used} provider`);
      }
      
      // Show debug info if available and not successful
      if (data.debug && !data.success) {
        console.warn('Audio question debug:', data.debug);
      }
    } catch (err: any) {
      console.error('Audio Question API Error:', err);
      console.error('Error details:', err.message, err.stack);
      setError(err.message || 'Failed to get answer from audio');
      
      // Remove the message if there was an error
      setChatHistory(prev => prev.filter(msg => msg.id !== newMessage.id));
    } finally {
      setLoading(false);
      setQuestion('');
    }
  };

  const getSuggestions = async () => {
    if (!selectedDocId) return;

    setLoadingSuggestions(true);
    setError(null);
    try {
      const data = await apiRequest('/documents/audio-suggestions', {
        method: 'POST',
        body: JSON.stringify({
          document_id: selectedDocId,
          language: language,
          provider: provider
        }),
      });
      
      // Handle new structured response format
      if (data.suggestions) {
        setSuggestions(data.suggestions);
        
        // Show provider info if available
        if (data.provider_used && data.provider_used !== provider) {
          setError(`Switched to ${data.provider_used} provider`);
        }
        
        // Show debug info if available and not successful
        if (data.debug && !data.success) {
          console.warn('Audio suggestions debug:', data.debug);
        }
      } else {
        setSuggestions([]);
      }
    } catch (err: any) {
      console.error('Audio Suggestions API Error:', err);
      setError('Failed to get audio suggestions');
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const generateSummary = async () => {
    if (!selectedDocId) return;

    setLoadingSummary(true);
    setError(null);
    try {
      const data = await apiRequest('/documents/audio-summary', {
        method: 'POST',
        body: JSON.stringify({
          document_id: selectedDocId,
          language: language,
          provider: provider
        }),
      });
      
      // Handle new structured response format
      if (data.summary) {
        setSummary(data.summary);
        
        // Show provider info if available
        if (data.provider_used && data.provider_used !== provider) {
          setError(`Switched to ${data.provider_used} provider`);
        }
        
        // Show debug info if available and not successful
        if (data.debug && !data.success) {
          console.warn('Audio summary debug:', data.debug);
        }
      } else {
        setSummary('');
      }
    } catch (err: any) {
      console.error('Audio Summary API Error:', err);
      setError('Failed to generate audio summary');
      setSummary('');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSend = async () => {
    await askQuestion();
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuestion(suggestion);
  };

  // Helper function to detect if text contains Hebrew characters
  const containsHebrew = (text: string) => {
    return /[\u0590-\u05FF]/.test(text);
  };

  // Helper function to render text with proper RTL support
  // Uses CSS unicode-bidi: isolate with targeted processing for parentheses
  const renderTextWithRTL = (text: string) => {
    try {
      if (!text || typeof text !== 'string') {
        return <div style={{ fontFamily: 'Inter, Open Sans, Arial, sans-serif' }}>No content</div>;
      }

      const isHebrew = containsHebrew(text);

      if (isHebrew) {
        // Process only parentheses content - wrap English/numbers inside parens with LTR isolate
        // This fixes issues like "(סוף Q3 ותחילת Q4)" where Q3/Q4 need LTR isolation
        const processedText = text.replace(
          /\(([^)]+)\)/g,
          (match, content) => {
            // Wrap English words and numbers inside parentheses with LTR isolate markers
            const fixedContent = content.replace(
              /([a-zA-Z0-9]+(?:\.[0-9]+)?)/g,
              '\u2066$1\u2069'  // LTR isolate + pop isolate
            );
            return `(${fixedContent})`;
          }
        );

        return (
          <div
            className="hebrew-text"
            style={{
              direction: 'rtl',
              textAlign: 'right',
              unicodeBidi: 'isolate',  // Create proper bidi isolation context
              fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif',
              lineHeight: '1.8',
              fontSize: '1rem',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap'
            }}
          >
            {processedText}
          </div>
        );
      }

      return (
        <div style={{ fontFamily: 'Inter, Open Sans, Arial, sans-serif' }}>
          {text}
        </div>
      );
    } catch (error) {
      console.error('Error rendering text with RTL:', error);
      return (
        <div style={{ fontFamily: 'Inter, Open Sans, Arial, sans-serif', color: 'red' }}>
          Error rendering content
        </div>
      );
    }
  };

  // Parse [N] citation markers in chat answers and make them clickable
  const renderWithCitations = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\[\d+\])/g);
    if (parts.length === 1) return null; // No citations found

    return parts.map((part, i) => {
      const match = part.match(/^\[(\d+)\]$/);
      if (match) {
        const paraIndex = parseInt(match[1], 10);
        return (
          <Box
            key={i}
            component="span"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const paraEl = document.getElementById(`transcript-para-${paraIndex}`);
              if (paraEl) {
                paraEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                paraEl.style.backgroundColor = 'rgba(74, 144, 226, 0.2)';
                setTimeout(() => { paraEl.style.backgroundColor = ''; }, 3000);
              }
            }}
            sx={{
              color: '#1976d2',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.8em',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {part}
          </Box>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString(language === 'Hebrew' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box 
      sx={{ 
        p: { xs: 0.5, sm: 1, md: 1.5 }, 
        fontFamily, 
        bgcolor: '#F7F9FB', 
        minHeight: '100vh',
        width: '100vw',
        overflowX: 'hidden'
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontFamily, 
            color: '#1A2341', 
            fontWeight: 700, 
            fontSize: { xs: 24, sm: 32 },
            mb: 2
          }}
        >
          Replay Center
        </Typography>
        
        {/* Main Layout with Sidebar */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1, width: '100%' }}>
          {/* Left Sidebar - Quick Navigation */}
          <Box sx={{ 
            width: { xs: '100%', lg: '280px' }, 
            flexShrink: 0,
            position: { xs: 'static', lg: 'sticky' },
            top: 20,
            height: 'fit-content'
          }}>
            <QuickReplayNavigation
              onReplaySelect={(docId) => {
                setSelectedDocId(docId);
                // Clear filters when selecting from quick navigation
                setSelectedAnalyst(null);
                setSelectedCompany(null);
                setSelectedSector(null);
                setSelectedTicker(null);
              }}
              selectedDocId={selectedDocId}
              language={language}
            />
          </Box>
          
          {/* Right Main Content */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Filters Section */}
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: 3, boxShadow: 2, bgcolor: '#fff', width: '100%' }}>
        <Grid container spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <Grid item xs={12} sm={2}>
            <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Analyst</Typography>
            <Autocomplete
              options={[{ id: '', name: 'All' }, ...analysts]}
              getOptionLabel={option => option.name || ''}
              value={analysts.find(a => a.id === selectedAnalyst) || { id: '', name: 'All' }}
              onChange={(_e, newValue) => setSelectedAnalyst(newValue?.id || null)}
              renderInput={params => <TextField {...params} variant="outlined" size="small" sx={{ fontFamily, mb: 2 }} fullWidth />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2, minWidth: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Company</Typography>
            <Autocomplete
              options={[{ id: '', name: 'All' }, ...filteredCompanies]}
              getOptionLabel={option => option.name || ''}
              value={filteredCompanies.find(c => c.id === selectedCompany) || { id: '', name: 'All' }}
              onChange={(_e, newValue) => setSelectedCompany(newValue?.id || null)}
              renderInput={params => <TextField {...params} variant="outlined" size="small" sx={{ fontFamily, mb: 2 }} fullWidth />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2, minWidth: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Sector</Typography>
            <Autocomplete
              options={[{ id: '', name: 'All' }, ...sectors]}
              getOptionLabel={option => option.name || ''}
              value={sectors.find(s => s.id === selectedSector) || { id: '', name: 'All' }}
              onChange={(_e, newValue) => setSelectedSector(newValue?.id || null)}
              renderInput={params => <TextField {...params} variant="outlined" size="small" sx={{ fontFamily, mb: 2 }} fullWidth />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2, minWidth: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Ticker</Typography>
            <Autocomplete
              options={[{ ticker: '', label: 'All' }, ...filteredTickerOptions.map(t => ({ ticker: t, label: t }))]}
              getOptionLabel={option => option.label || ''}
              value={filteredTickerOptions.find(t => t === selectedTicker) ? { ticker: selectedTicker, label: selectedTicker } : { ticker: '', label: 'All' }}
              onChange={(_e, newValue) => setSelectedTicker(newValue?.ticker || null)}
              renderInput={params => <TextField {...params} variant="outlined" size="small" sx={{ fontFamily, mb: 2 }} fullWidth />}
              isOptionEqualToValue={(option, value) => option.ticker === value.ticker}
              sx={{ mb: 2, minWidth: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography fontWeight={600} sx={{ fontFamily, fontSize: { xs: 14, sm: 16 } }}>Audio Document</Typography>
            <Autocomplete
              options={filteredDocuments}
              getOptionLabel={option => option.title || ''}
              value={filteredDocuments.find(d => d.id === selectedDocId) || null}
              onChange={(_e, newValue) => setSelectedDocId(newValue?.id || null)}
              renderInput={params => <TextField {...params} variant="outlined" size="small" sx={{ fontFamily, mb: 2 }} fullWidth />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2, minWidth: 0 }}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Typography fontWeight={600} sx={{ fontFamily }}>Language:</Typography>
          <Select
            value={language}
            onChange={e => setLanguage(e.target.value as 'English' | 'Hebrew')}
            size="small"
            sx={{ width: 120, fontFamily }}
          >
            <MenuItem value="English">English</MenuItem>
            <MenuItem value="Hebrew">Hebrew</MenuItem>
          </Select>
          <Typography fontWeight={600} sx={{ fontFamily }}>Provider:</Typography>
          <Select
            value={provider}
            onChange={e => setProvider(e.target.value as 'openai' | 'gemini' | 'xai')}
            size="small"
            sx={{ width: 120, fontFamily }}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="gemini">Gemini</MenuItem>
          </Select>
        </Box>
      </Paper>

      {/* Main Content Area - Audio Player and Chat Side by Side */}
      <Grid container spacing={3} sx={{ height: 'calc(100vh - 280px)', minHeight: 600, width: '100%' }}>
        {/* Audio Player & Transcript - Left Side */}
        <Grid item xs={12} lg={6} sx={{ height: '100%', minHeight: 0 }}>
          {selectedDoc && selectedDoc.storage_url ? (
            <Paper sx={{ 
              borderRadius: 3, 
              boxShadow: 2, 
              bgcolor: '#fff',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden' // Ensure content doesn't spill out
            }}>
              
              {/* 1. Header & Compact Player Section */}
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #eee',
                bgcolor: '#f8f9fa'
              }}>
                {/* Title */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontFamily, color: '#1A2341', fontWeight: 700, lineHeight: 1.2 }}>
                      {selectedDoc.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontFamily }}>
                      {selectedDoc.filename}
                    </Typography>
                  </Box>
                  <Chip 
                    label="Audio" 
                    color="primary" 
                    size="small" 
                    sx={{ fontFamily, height: 24 }} 
                  />
                </Box>

                {/* Compact Player Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  bgcolor: '#fff',
                  p: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  {/* Play/Pause Button */}
                  <Button
                    variant="contained"
                    onClick={togglePlayPause}
                    sx={{ 
                      minWidth: 40, 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      p: 0,
                      bgcolor: '#1A2341',
                      flexShrink: 0
                    }}
                  >
                    {isPlaying ? <PauseIcon sx={{ fontSize: 24 }} /> : <PlayArrowIcon sx={{ fontSize: 24 }} />}
                  </Button>

                  {/* Progress Bar & Time */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ 
                      width: '100%', 
                      height: 6, 
                      bgcolor: '#e0e0e0', 
                      borderRadius: 3, 
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onClick={(e) => {
                      if (!audioPlayer) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percent = x / rect.width;
                      audioPlayer.currentTime = percent * duration;
                    }}
                    >
                      <Box sx={{ 
                        width: `${(currentTime / duration) * 100}%`, 
                        height: '100%', 
                        bgcolor: '#1A2341',
                        transition: 'width 0.1s linear'
                      }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#666', fontFamily }}>
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </Box>
                  </Box>

                  {/* Volume Icon (Optional/Decorative for now) */}
                  <VolumeUpIcon sx={{ color: '#999', fontSize: 20 }} />
                </Box>

              </Box>

              {/* 3. Transcript View (Scrollable) */}
              <Box 
                ref={transcriptRef}
                sx={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  p: 2,
                  bgcolor: '#fff',
                  scrollBehavior: 'smooth',
                  minHeight: 0
                }}
              >
                {/* Transcript Header with Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontFamily, color: '#999', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                    {language === 'Hebrew' ? 'תמליל השיחה (לחץ לניגון)' : 'Live Transcript (Click to Play)'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={language === 'Hebrew' ? 'העתק תמליל' : 'Copy Transcript'}>
                      <IconButton size="small" onClick={handleCopyTranscript} disabled={!selectedDoc?.text_content}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={language === 'Hebrew' ? 'ייצוא ל-PDF' : 'Export to PDF'}>
                      <IconButton size="small" onClick={handleExportTranscriptPDF} disabled={!selectedDoc?.text_content}>
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                {transcriptLines.length > 0 ? (
                  transcriptLines.map((line, index) => {
                    // Determine if this is the active line
                    // A line is active if currentTime is >= line.time AND currentTime < nextLine.time
                    const isActive = line.time !== null && 
                                     currentTime >= line.time && 
                                     (index === transcriptLines.length - 1 || !transcriptLines[index+1].time || currentTime < transcriptLines[index+1].time!);
                    
                    return (
                      <Box
                        key={index}
                        id={`transcript-para-${index}`}
                        ref={isActive ? activeLineRef : null}
                        onClick={() => handleTranscriptClick(line.time)}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          cursor: line.time !== null ? 'pointer' : 'default',
                          bgcolor: isActive ? '#E3F2FD' : 'transparent',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            bgcolor: line.time !== null ? '#f5f5f5' : 'transparent'
                          },
                          mb: 0.5
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {line.time !== null && (
                            <Typography sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem', 
                              color: isActive ? '#1976d2' : '#999',
                              minWidth: 45,
                              flexShrink: 0,
                              mt: 0.3
                            }}>
                              {formatTime(line.time)}
                            </Typography>
                          )}
                          <Typography sx={{ 
                            fontFamily, 
                            fontSize: '0.9rem', 
                            lineHeight: 1.5,
                            color: isActive ? '#1A2341' : '#444',
                            fontWeight: isActive ? 500 : 400,
                            direction: containsHebrew(line.text) ? 'rtl' : 'ltr',
                            textAlign: containsHebrew(line.text) ? 'right' : 'left'
                          }}>
                            {line.text.replace(/\[\d{2}:\d{2}\]/, '').trim()} 
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ color: '#aaa', fontFamily, textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
                    {language === 'Hebrew' ? 'אין תמליל זמין' : 'No transcript available'}
                  </Typography>
                )}
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ 
              p: 4, 
              borderRadius: 3, 
              boxShadow: 2, 
              bgcolor: '#fff',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography sx={{ color: '#888', fontFamily, textAlign: 'center' }}>
                {language === 'Hebrew' 
                  ? 'בחר מסמך אודיו כדי להתחיל' 
                  : 'Select an audio document to get started'
                }
              </Typography>
            </Paper>
          )}
        </Grid>

        {/* Chat Interface - Right Side */}
        <Grid item xs={12} lg={6} sx={{ height: '100%', minHeight: 0 }}>
          <Paper sx={{ 
            borderRadius: 3, 
            boxShadow: 2, 
            bgcolor: '#fff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} aria-label="chat tabs" variant="fullWidth">
                <Tab label={language === 'Hebrew' ? "צ'אט" : "Chat"} sx={{ fontFamily, fontWeight: 600 }} />
                <Tab label={language === 'Hebrew' ? "סיכום" : "Summary"} sx={{ fontFamily, fontWeight: 600 }} />
              </Tabs>
            </Box>

            {/* Tab 0: Chat */}
            <Box role="tabpanel" hidden={activeTab !== 0} sx={{ flex: 1, display: activeTab === 0 ? 'flex' : 'none', flexDirection: 'column', minHeight: 0, p: { xs: 1, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={getSuggestions}
                  disabled={loadingSuggestions || !selectedDocId}
                  sx={{ fontFamily, borderColor: '#1A2341', color: '#1A2341', fontSize: '0.75rem' }}
                >
                  {loadingSuggestions ? <CircularProgress size={16} /> : (language === 'Hebrew' ? 'הצעות שאלות' : 'Suggestions')}
                </Button>
              </Box>
            
            {/* Suggestions */}
            {suggestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontFamily, color: '#666', mb: 1 }}>
                  {language === 'Hebrew' ? 'הצעות שאלות:' : 'Question Suggestions:'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {suggestions.map((suggestion, index) => (
                    <Paper
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{ 
                        p: { xs: 1, sm: 1.5 }, 
                        bgcolor: '#E3F2FD', 
                        borderRadius: 2, 
                        cursor: 'pointer',
                        fontFamily,
                        '&:hover': {
                          bgcolor: '#BBDEFB',
                        },
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        direction: containsHebrew(suggestion) ? 'rtl' : 'ltr',
                        textAlign: containsHebrew(suggestion) ? 'right' : 'left',
                        unicodeBidi: 'plaintext',
                        minHeight: { xs: 'auto', sm: 'auto' }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily,
                          lineHeight: 1.4,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {suggestion}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Chat History */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              mb: 2,
              px: 1
            }}>
              {chatHistory.length === 0 && (
                <Typography sx={{ color: '#888', fontFamily, textAlign: 'center', mt: 4 }}>
                  {language === 'Hebrew' 
                    ? 'התחל לצטט על הקובץ האודיו שנבחר!' 
                    : 'Start chatting about your selected audio file!'
                  }
                </Typography>
              )}
              {chatHistory.map((msg) => {
                try {
                  return (
                    <Box key={msg.id} sx={{ mb: 2 }}>
                      {/* Question */}
                      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Paper 
                          sx={{ 
                            p: 1.5, 
                            bgcolor: '#E3F2FD', 
                            borderRadius: 2, 
                            maxWidth: '85%', 
                            fontFamily, 
                            boxShadow: 1
                          }}
                        >
                          {renderTextWithRTL(msg.question || '')}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666', fontSize: '0.7rem' }}>
                            {formatDate(msg.timestamp)}
                          </Typography>
                        </Paper>
                      </Box>
                      
                      {/* Answer */}
                      {msg.answer && (
                        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
                          <Paper 
                            sx={{ 
                              p: 1.5, 
                              bgcolor: '#F7F9FB', 
                              borderRadius: 2, 
                              maxWidth: '85%', 
                              fontFamily, 
                              boxShadow: 1
                            }}
                          >
                            {renderWithCitations(msg.answer) || renderTextWithRTL(msg.answer)}
                            {msg.processing_time && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666', fontSize: '0.7rem' }}>
                                {language === 'Hebrew' ? 'זמן עיבוד:' : 'Processing time:'} {msg.processing_time.toFixed(2)}s
                              </Typography>
                            )}
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  );
                } catch (error) {
                  console.error('Error rendering chat message:', error, msg);
                  return (
                    <Box key={msg.id} sx={{ mb: 2 }}>
                      <Paper sx={{ p: 1.5, bgcolor: '#ffebee', borderRadius: 2, maxWidth: '85%' }}>
                        <Typography color="error">Error rendering message</Typography>
                      </Paper>
                    </Box>
                  );
                }
              })}
              <div ref={chatEndRef} />
            </Box>

            {/* Input Area */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              pt: 1,
              borderTop: '1px solid #eee'
            }}>
              <TextField
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !loading && question.trim() && selectedDocId) handleSend(); }}
                placeholder={language === 'Hebrew' 
                  ? "שאל כל שאלה על הקובץ האודיו..." 
                  : "Ask anything about the audio file..."
                }
                variant="outlined"
                size="small"
                sx={{ 
                  fontFamily, 
                  flex: 1,
                  '& .MuiInputBase-input': {
                    direction: containsHebrew(question) ? 'rtl' : 'ltr',
                    textAlign: containsHebrew(question) ? 'right' : 'left',
                    fontSize: '0.9rem'
                  }
                }}
                disabled={loading}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ 
                  fontFamily, 
                  bgcolor: '#1A2341', 
                  color: '#fff', 
                  fontWeight: 700,
                  minWidth: 'auto',
                  px: 2
                }}
                onClick={handleSend}
                disabled={loading || !question.trim() || !selectedDocId}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </Button>
            </Box>
            </Box>

            {/* Tab 1: Summary */}
            <Box role="tabpanel" hidden={activeTab !== 1} sx={{ flex: 1, display: activeTab === 1 ? 'flex' : 'none', flexDirection: 'column', minHeight: 0, p: 3, overflowY: 'auto' }}>
              {!summary ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                  <Typography sx={{ color: '#666', fontFamily }}>
                    {language === 'Hebrew' ? 'אין סיכום זמין למסמך זה' : 'No summary available for this document'}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={generateSummary}
                    disabled={loadingSummary || !selectedDocId}
                    sx={{ fontFamily, bgcolor: '#1A2341', color: '#fff' }}
                  >
                    {loadingSummary ? <CircularProgress size={20} color="inherit" /> : (language === 'Hebrew' ? 'צור סיכום' : 'Generate Summary')}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                     <Tooltip title={language === 'Hebrew' ? 'העתק טקסט' : 'Copy Text'}>
                       <IconButton onClick={handleCopySummary} size="small">
                         <ContentCopyIcon fontSize="small" />
                       </IconButton>
                     </Tooltip>
                  </Box>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2, 
                    border: '1px solid #eee',
                    whiteSpace: 'pre-wrap' // Preserve newlines
                  }}>
                    {renderTextWithRTL(summary)}
                  </Box>
                </Box>
              )}
            </Box>
            
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={2000}
              onClose={() => setSnackbarOpen(false)}
              message={language === 'Hebrew' ? 'הטקסט הועתק!' : 'Text copied!'}
            />
          </Paper>
        </Grid>
      </Grid>
      
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        </Box>
      </Box>
    </Box>
  );
} 